/**
 * This module tracks the diff edit history of cells for our typeahead model.
 * It aggregates character-level diffs from monaco into edit events, and then formats them
 * in the format our model has been trained on.
 */
import { CellId, CircularBuffer } from "@hex/common";
import { createPatch } from "diff";
import { editor as Editor, IDisposable } from "monaco-editor";

export interface EditEvent {
  timestamp: number;
  cellId: CellId;
  beforeContent: string;
  afterContent: string;
  versionId: number;
  startVersionId: number;
}

export interface FormattedEditEvent {
  cellId: CellId;
  diff: string;
  timestamp: number;
}

class EditHistoryManager {
  private editHistoryByModelUri: Record<string, CircularBuffer<EditEvent>> = {};
  private disposablesByModelUri: Record<string, IDisposable> = {};
  private lastContentByModelUri: Record<string, string> = {};
  private isInitializedByModelUri: Record<string, boolean> = {};

  private static readonly MAX_HISTORY_SIZE = 10;
  private static readonly EDIT_COALESCING_WINDOW_MS = 2000;
  private static readonly MAX_DIFF_SIZE = 3000;
  private static readonly LARGE_EDIT_SUMMARY = "[Large edit - diff truncated]";

  registerModel(model: Editor.ITextModel, cellId: CellId): void {
    const modelUri = model.uri.toString();

    if (this.disposablesByModelUri[modelUri]) {
      return;
    }

    this.editHistoryByModelUri[modelUri] ??= new CircularBuffer<EditEvent>(
      EditHistoryManager.MAX_HISTORY_SIZE,
    );

    const initialContent = model.getValue();
    this.lastContentByModelUri[modelUri] = initialContent;

    // Mark as initialized if there's already content
    this.isInitializedByModelUri[modelUri] = initialContent.length > 0;

    const disposable = model.onDidChangeContent((event) => {
      this.handleModelContentChange(model, cellId, event);
    });

    this.disposablesByModelUri[modelUri] = disposable;
  }

  unregisterModel(model: Editor.ITextModel): void {
    const modelUri = model.uri.toString();

    const disposable = this.disposablesByModelUri[modelUri];
    if (disposable) {
      disposable.dispose();
      delete this.disposablesByModelUri[modelUri];
    }

    delete this.editHistoryByModelUri[modelUri];
    delete this.lastContentByModelUri[modelUri];
    delete this.isInitializedByModelUri[modelUri];
  }

  private handleModelContentChange(
    model: Editor.ITextModel,
    cellId: CellId,
    event: Editor.IModelContentChangedEvent,
  ): void {
    const modelUri = model.uri.toString();
    const currentContent = model.getValue();
    const currentVersionId = event.versionId;
    const beforeContent = this.lastContentByModelUri[modelUri] || "";

    if (beforeContent === currentContent) {
      return;
    }

    if (!this.isInitializedByModelUri[modelUri]) {
      this.isInitializedByModelUri[modelUri] = true;
      this.lastContentByModelUri[modelUri] = currentContent;
      return;
    }

    const now = Date.now();

    const historyBuffer = (this.editHistoryByModelUri[modelUri] ??=
      new CircularBuffer<EditEvent>(EditHistoryManager.MAX_HISTORY_SIZE));

    // Check to see if we ought to coalesce with the last event
    const lastEvent = historyBuffer.getLast();
    const shouldCoalesce =
      lastEvent &&
      now - lastEvent.timestamp <=
        EditHistoryManager.EDIT_COALESCING_WINDOW_MS &&
      lastEvent.cellId === cellId &&
      lastEvent.versionId === currentVersionId - 1;
    if (shouldCoalesce) {
      // Pop the last event and push a coalesced version
      historyBuffer.popLast();
      const coalescedEvent: EditEvent = {
        ...lastEvent,
        afterContent: currentContent,
        timestamp: now,
        versionId: currentVersionId,
      };
      historyBuffer.push(coalescedEvent);
    } else {
      const editEvent: EditEvent = {
        timestamp: now,
        cellId,
        beforeContent,
        afterContent: currentContent,
        versionId: currentVersionId,
        startVersionId: currentVersionId,
      };

      historyBuffer.push(editEvent);
    }

    this.lastContentByModelUri[modelUri] = currentContent;
  }

  getAllRecentEditHistory(limit: number = 10): FormattedEditEvent[] {
    const allEdits: EditEvent[] = [];

    for (const modelUri in this.editHistoryByModelUri) {
      const buffer = this.editHistoryByModelUri[modelUri];
      const modelEdits = buffer.getRecent(limit);
      allEdits.push(...modelEdits);
    }

    allEdits.sort((a, b) => b.timestamp - a.timestamp);
    const recentEdits = allEdits.slice(0, limit);

    // model is trained to see diffs in reverse chronological order (newest edits last)
    return recentEdits
      .toReversed()
      .map((editEvent) => ({
        cellId: editEvent.cellId,
        timestamp: editEvent.timestamp,
        diff: this.generateUnifiedDiff(
          editEvent.cellId,
          editEvent.beforeContent,
          editEvent.afterContent,
        ),
      }))
      .filter((formatted) => formatted.diff.trim().length > 0);
  }

  clearAllEditHistory(): void {
    for (const modelUri in this.editHistoryByModelUri) {
      this.editHistoryByModelUri[modelUri].clear();
    }
  }

  generateUnifiedDiff(
    cellId: CellId,
    beforeContent: string,
    afterContent: string,
  ): string {
    if (beforeContent === afterContent) {
      return "";
    }

    const patch = createPatch(
      `cell_${cellId.slice(-8)}`, // Use cell suffix for smaller unique-ish id
      beforeContent,
      afterContent,
      "",
      "",
      { context: 3 },
    );

    const lines = patch.split("\n");
    const cleanedLines = lines
      .slice(4) // patch diffs default output some lines we don't care about. This skips "Index:", "===", "---", and "+++" lines
      .filter((line) => !line.startsWith("\\ No newline at end of file"));

    const diff = cleanedLines.join("\n");

    // Check if the diff is enormous
    if (diff.length > EditHistoryManager.MAX_DIFF_SIZE) {
      const totalSize = beforeContent.length + afterContent.length;
      if (totalSize > EditHistoryManager.MAX_DIFF_SIZE * 4) {
        const linesBefore = beforeContent.split("\n").length;
        const linesAfter = afterContent.split("\n").length;
        const lineChange = linesAfter - linesBefore;
        const changeSign = lineChange >= 0 ? "+" : "";

        return `@@ -1,${linesBefore} +1,${linesAfter} @@\n${EditHistoryManager.LARGE_EDIT_SUMMARY}\n[${changeSign}${lineChange} lines, ~${Math.abs(afterContent.length - beforeContent.length)} chars]`;
      } else {
        // Truncate the diff if so
        const truncated = diff.substring(
          0,
          EditHistoryManager.MAX_DIFF_SIZE - 50,
        );
        const lastNewline = truncated.lastIndexOf("\n");
        return truncated.substring(0, lastNewline) + "\n... [diff truncated]";
      }
    }

    return diff;
  }
}

export const editHistoryManager = new EditHistoryManager();
